import { clientSDK, withFields } from "@istari-frontend/api-client";
import {
  BranchCommitDto,
  BranchFileChange,
  NewSystemConfiguration,
  NewTrackedFile,
  NewTrackedSystem,
  TrackedFileSpecifierType,
} from "@istari/istari-client";

import type { BranchChange, BranchCommitted, TrackedFileChoice } from "~/branching/model/branch-change";

export const branchCommitCommands = {
  commit: async (
    systemId: string,
    branchId: string,
    expectedHeadSnapshotId: string,
    change: BranchChange,
  ): Promise<BranchCommitted> => {
    const committed = await clientSDK.commitBranch(
      systemId,
      toBranchCommitDto(branchId, expectedHeadSnapshotId, change),
    );
    return { snapshotId: committed.newSnapshotId, configurationId: committed.configurationId };
  },
  headSnapshotId: async (branchId: string): Promise<string> => (await clientSDK.getTag(branchId)).snapshotId,
  configurationId: async (snapshotId: string): Promise<string> =>
    (await clientSDK.getSnapshot(snapshotId)).configurationId,
};

function toBranchCommitDto(branchId: string, expectedHeadSnapshotId: string, change: BranchChange): BranchCommitDto {
  const dto = new BranchCommitDto(branchId, expectedHeadSnapshotId);
  switch (change.kind) {
    case "head":
      return withFields(dto, { commitMessage: change.message });
    case "put":
      return withFields(dto, {
        commitMessage: change.message,
        put: change.fileRevisionIds.map((revisionId) => new BranchFileChange(revisionId)),
      });
    case "configuration": {
      const { name, trackedFiles, trackedSystems } = change.configuration;
      return withFields(dto, {
        commitMessage: change.message,
        _configuration: new NewSystemConfiguration(
          name,
          trackedFiles.map(toNewTrackedFile),
          trackedSystems.map(({ systemId, tagId }) => new NewTrackedSystem(systemId, tagId)),
        ),
      });
    }
  }
}

function toNewTrackedFile(file: TrackedFileChoice): NewTrackedFile {
  return file.follows === "revision"
    ? new NewTrackedFile(TrackedFileSpecifierType.Locked, file.fileId, file.revisionId)
    : new NewTrackedFile(TrackedFileSpecifierType.Latest, file.fileId);
}
